import React from "react";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";

function SEO({
    title,
    description,
    image,
    pathname,
    article,
    datePublished,
    dateModified,
    keywords,
    author,
    lang,
    meta,
    noindex,
}) {
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        description
                        siteUrl
                        author
                        image
                        twitterUsername
                        lang
                    }
                }
            }
        `
    );

    const sm = site.siteMetadata;
    const siteUrl = sm.siteUrl.replace(/\/$/, "");
    const metaTitle = title || sm.title;
    const metaDescription = description || sm.description;
    const metaImage = image
        ? (image.startsWith("http") ? image : `${siteUrl}${image}`)
        : `${siteUrl}${sm.image}`;
    const canonical = pathname
        ? `${siteUrl}${pathname.startsWith("/") ? pathname : `/${pathname}`}`
        : siteUrl;
    const metaAuthor = author || sm.author;
    const metaLang = lang || sm.lang || "en";

    const jsonLd = article
        ? {
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
              headline: metaTitle,
              description: metaDescription,
              image: [metaImage],
              datePublished: datePublished || undefined,
              dateModified: dateModified || datePublished || undefined,
              author: { "@type": "Organization", name: metaAuthor },
              publisher: {
                  "@type": "Organization",
                  name: sm.title,
                  logo: {
                      "@type": "ImageObject",
                      url: `${siteUrl}${sm.image}`,
                  },
              },
          }
        : {
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: sm.title,
              url: siteUrl,
              description: metaDescription,
          };

    const metaTags = [
        { name: "description", content: metaDescription },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: article ? "article" : "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: metaImage },
        { property: "og:site_name", content: sm.title },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: metaDescription },
        { name: "twitter:image", content: metaImage },
    ];

    if (sm.twitterUsername) {
        metaTags.push({ name: "twitter:site", content: sm.twitterUsername });
        metaTags.push({ name: "twitter:creator", content: sm.twitterUsername });
    }
    if (article && datePublished) {
        metaTags.push({ property: "article:published_time", content: datePublished });
    }
    if (article && (dateModified || datePublished)) {
        metaTags.push({
            property: "article:modified_time",
            content: dateModified || datePublished,
        });
    }
    if (article && metaAuthor) {
        metaTags.push({ property: "article:author", content: metaAuthor });
    }
    if (keywords && keywords.length > 0) {
        metaTags.push({ name: "keywords", content: keywords.join(", ") });
    }
    if (noindex) {
        metaTags.push({ name: "robots", content: "noindex,nofollow" });
    }

    return (
        <Helmet
            htmlAttributes={{ lang: metaLang }}
            title={metaTitle}
            titleTemplate={title ? `%s | ${sm.title}` : `%s`}
            meta={metaTags.concat(meta || [])}
            link={[{ rel: "canonical", href: canonical }]}
        >
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
    );
}

SEO.defaultProps = {
    article: false,
    meta: [],
    keywords: [],
    noindex: false,
};

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    pathname: PropTypes.string,
    article: PropTypes.bool,
    datePublished: PropTypes.string,
    dateModified: PropTypes.string,
    keywords: PropTypes.arrayOf(PropTypes.string),
    author: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.array,
    noindex: PropTypes.bool,
};

export default SEO;
